import { createGlobalStyle } from "styled-components";

/**
 * Reset global da lib @ficatranquilo/contabilidade.com-ui: o `reset.css` do
 * ft-app-web (o mesmo do playground da lib), de que os componentes dependem
 * (botões e inputs sem borda nativa, links herdando a cor, `svg` contido).
 *
 * Fica no cascade layer `ow-reset`: estilo sem layer vence qualquer layer, então
 * o reset só ganha do navegador e perde para a lib, como um reset carregado antes.
 * Sem o layer, ele entraria no mesmo <style> dos componentes, depois deles, e o
 * `[type='button'] { background: none }` apagaria o fundo do botão primary.
 *
 * Diferenças para o reset do ft-app-web:
 * - `appearance: none` só em input, button, textarea e select com classe (os da
 *   lib). Checkbox, radio e select sem classe ficam com a aparência nativa.
 * - `body` sem `overflow` próprio: com o `overflow-y: scroll` do `html`, ele
 *   viraria o contêiner de rolagem e o `position: sticky` pararia de funcionar.
 * - `color` e `background` no `body`, para o texto sem cor própria da lib.
 */
const LibGlobalStyle = createGlobalStyle`
  @layer ow-reset {
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    * {
      padding: 0;
      margin: 0;
      border: 0;
      outline: none;
    }

    html {
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
      scroll-behavior: smooth;
      overflow-y: scroll;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    body {
      min-height: 100dvh;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      color: ${({ theme }) => theme.colors.N900};
      background-color: ${({ theme }) => theme.colors.N50};
      text-rendering: optimizeLegibility;
      -webkit-tap-highlight-color: transparent;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      text-wrap: balance;
      line-height: 1.2;
    }

    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    blockquote {
      overflow-wrap: break-word;
    }

    ul,
    ol {
      list-style: none;
    }

    img,
    picture,
    video,
    canvas {
      display: block;
      max-width: 100%;
    }

    img,
    video {
      height: auto;
    }

    svg {
      max-width: 100%;
      height: auto;
      vertical-align: middle;
    }

    a {
      color: inherit;
      text-decoration: none;
      cursor: pointer;
      text-decoration-skip-ink: auto;
    }

    table {
      border-collapse: collapse;
      border-spacing: 0;
      width: 100%;
    }

    input,
    button,
    textarea,
    select {
      font: inherit;
      color: inherit;
      letter-spacing: inherit;
    }

    :where(input[class], button[class], textarea[class], select[class]) {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    :where(button, [type='button'], [type='submit'], [type='reset']) {
      background: none;
      line-height: inherit;
      cursor: pointer;
    }

    textarea {
      resize: vertical;
    }

    @media (prefers-contrast: more) {
      :where(:focus-visible) {
        outline-width: 3px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
        scroll-behavior: auto !important;
      }
    }

    .no-scroll {
      overflow-y: hidden;
    }
  }
`;

export default LibGlobalStyle;
